/**
 * `useCommsChannels(party)` — the one hook every surface renders channels from (D8).
 *
 * Merges three sources into one `CommsChannelOption` per channel:
 *   • `/comms/channels` — which channels exist and which the tenant can use
 *     (shared, cached 5 min);
 *   • `/comms/reachability` — whether THIS party can be reached from the tenant's
 *     own connection (level, window, reason), batched per party ref;
 *   • the party's raw address the surface already holds — so the hand-off
 *     entries (mailto:, wa.me) render synchronously and no request is made until
 *     the surface opens its menu (`enabled`).
 *
 * Degradation: while `/channels` has not answered (or failed), the list falls
 * back to the hand-off channels, so a network problem yields today's menu, never
 * an empty one. Reachability only ever ADDS in-app entries.
 */
import { useMemo } from 'react';

import {
  getCommsReachabilityApiV1CommsReachabilityGetQueryKey,
  useCommsReachabilityApiV1CommsReachabilityGet,
  useListCommsChannelsApiV1CommsChannelsGet,
} from '../../generated/api/shared-comms/shared-comms';
import type { CommsChannelAvailability } from '../../generated/api/models/commsChannelAvailability';
import type { CommsPartyReachability } from '../../generated/api/models/commsPartyReachability';
import type { CommsReachability } from '../../generated/api/models/commsReachability';
import { HANDOFF_CHANNEL_IDS, presentationFor, rawAddressFor } from './channelPresentation';
import type { CommsChannelId, CommsChannelOption, CommsPartyLookup } from './types';

const AVAILABILITY_STALE_MS = 5 * 60 * 1000;
const REACHABILITY_STALE_MS = 60 * 1000;

/**
 * The query-key PREFIX of every `/comms/reachability` read, for a surface that
 * learns the answer has moved — an inbound WhatsApp message opens the 24h window,
 * so a thread's live-refresh cue invalidates this alongside the thread itself
 * rather than leaving the picker on a closed window for `REACHABILITY_STALE_MS`.
 */
export const COMMS_REACHABILITY_QUERY_KEY = getCommsReachabilityApiV1CommsReachabilityGetQueryKey();

const NO_CAPABILITIES = {
  outbound: true,
  inbound: false,
  attachments: false,
  templates: false,
  needs_window: false,
  proactive_to_unknown_party: true,
  client_handoff: true,
};

/** Every registered channel with the tenant's availability (shared across surfaces). */
export function useCommsChannelAvailability(enabled = true) {
  return useListCommsChannelsApiV1CommsChannelsGet({
    query: {
      enabled,
      staleTime: AVAILABILITY_STALE_MS,
      select: (response) => response.channels,
    },
  });
}

function idParties(parties: readonly CommsPartyLookup[]) {
  const contact_id = parties.map((p) => p.contact_id).filter((id): id is string => !!id);
  const organization_id = parties
    .map((p) => p.organization_id)
    .filter((id): id is string => !!id);
  const participant_id = parties
    .map((p) => p.participant_id)
    .filter((id): id is string => !!id);
  return {
    contact_id: contact_id.length ? contact_id : undefined,
    organization_id: organization_id.length ? organization_id : undefined,
    participant_id: participant_id.length ? participant_id : undefined,
    count: contact_id.length + organization_id.length + participant_id.length,
  };
}

/** Batched per-party reachability for every id party in `parties`. */
export function useCommsReachability(parties: readonly CommsPartyLookup[], enabled = true) {
  const { count, ...params } = idParties(parties);
  return useCommsReachabilityApiV1CommsReachabilityGet(params, {
    query: {
      enabled: enabled && count > 0,
      staleTime: REACHABILITY_STALE_MS,
      select: (response) => response.parties,
    },
  });
}

function rowFor(
  party: CommsPartyLookup | undefined,
  rows: readonly CommsPartyReachability[] | undefined,
): CommsPartyReachability | undefined {
  if (!party || !rows) return undefined;
  return rows.find(
    (row) =>
      (row.party.kind === 'contact' && row.party.id === party.contact_id) ||
      (row.party.kind === 'organization' && row.party.id === party.organization_id) ||
      (row.party.kind === 'participant' && row.party.id === party.participant_id),
  );
}

export function mergeChannelOption(
  channel: Pick<CommsChannelAvailability, 'id' | 'label' | 'capabilities'> & {
    available: boolean | undefined;
  },
  party: CommsPartyLookup | undefined,
  reachability: CommsReachability | undefined,
): CommsChannelOption {
  const presentation = presentationFor(channel.id);
  const address = reachability?.address ?? rawAddressFor(channel.id, party);
  const addressPresent = reachability?.address_present ?? address !== null;
  return {
    id: channel.id,
    label: channel.label,
    capabilities: channel.capabilities,
    available: channel.available,
    addressPresent,
    address,
    reachable: reachability?.reachable,
    level: reachability?.level ?? null,
    windowOpen: reachability?.window_open ?? null,
    windowExpiresAt: reachability?.window_expires_at ?? null,
    reason: reachability?.reason ?? (addressPresent ? null : 'no_address'),
    handoffUrl: (opts = {}) =>
      address !== null && presentation.buildHandoffUrl
        ? presentation.buildHandoffUrl(address, opts)
        : null,
  };
}

export interface UseCommsChannelsOptions {
  /** Fetch availability + reachability (typically: the menu is open). Default true. */
  enabled?: boolean;
}

export interface UseCommsChannelsResult {
  channels: CommsChannelOption[];
  /** `/comms/channels` in flight (list may still be the hand-off fallback). */
  isLoadingAvailability: boolean;
  /** `/comms/reachability` in flight for this party. */
  isLoadingReachability: boolean;
}

/**
 * The channels a surface renders for one party. Without a party (nothing to reach
 * yet) the list is availability-only: every channel with no address behind it.
 */
export function useCommsChannels(
  party: CommsPartyLookup | undefined,
  { enabled = true }: UseCommsChannelsOptions = {},
): UseCommsChannelsResult {
  const availability = useCommsChannelAvailability(enabled);
  const parties = useMemo(() => (party ? [party] : []), [party]);
  const reachability = useCommsReachability(parties, enabled);
  const row = rowFor(party, reachability.data);

  const channels = useMemo(() => {
    const base: Array<
      Pick<CommsChannelAvailability, 'id' | 'label' | 'capabilities'> & {
        available: boolean | undefined;
      }
    > =
      availability.data ??
      HANDOFF_CHANNEL_IDS.map((id: CommsChannelId) => ({
        id,
        label: id,
        capabilities: NO_CAPABILITIES,
        available: undefined,
      }));
    return base.map((channel) =>
      mergeChannelOption(
        channel,
        party,
        row?.channels.find((answer) => answer.channel === channel.id),
      ),
    );
  }, [availability.data, party, row]);

  return {
    channels,
    isLoadingAvailability: availability.isLoading,
    isLoadingReachability: reachability.isLoading,
  };
}
